/**
 * Owner: Masa Bayu
 * File: useTextSelection.ts
 * Date:Time: 2026-10-05 15:32
 * Description: Fasa 27 · mouse → hit-test → selection → highlight boxes.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefCallback,
} from 'react';
import {
  calculateHighlightBoxes,
  hitTestAtom,
  updateSelection,
  type HighlightBox,
  type HitTestAtom,
  type SelectionPoint,
  type SelectionRange,
} from '@qx/qx-interaction-engine';

export type UseTextSelectionResult = {
  readonly highlightBoxes: readonly HighlightBox[];
  readonly selectionRange: SelectionRange | null;
  /** Attach to the page sheet (bbox coordinate space). */
  readonly sheetRef: RefCallback<HTMLDivElement>;
};

function pageCoords(
  e: MouseEvent,
  container: HTMLElement,
  pageWidth: number,
  pageHeight: number,
): { x: number; y: number } {
  const rect = container.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return { x: 0, y: 0 };
  return {
    x: ((e.clientX - rect.left) / rect.width) * pageWidth,
    y: ((e.clientY - rect.top) / rect.height) * pageHeight,
  };
}

function hitPoint(
  atoms: readonly HitTestAtom[],
  x: number,
  y: number,
): SelectionPoint | null {
  for (const atom of atoms) {
    const result = hitTestAtom(atom, x, y);
    if (result.isHit && result.atomId != null && result.wordIndex != null) {
      return { atomId: result.atomId, wordIndex: result.wordIndex };
    }
  }
  return null;
}

/**
 * Sheet-level listeners · page-space coords · Engine 5 pure math.
 * sheetRef callback binds mousedown when the sheet mounts (avoids null-ref miss).
 */
export function useTextSelection(
  bodyAtoms: readonly HitTestAtom[],
  pageSize: { readonly width: number; readonly height: number },
): UseTextSelectionResult {
  const [selectionRange, setSelectionRange] = useState<SelectionRange | null>(null);
  const [highlightBoxes, setHighlightBoxes] = useState<HighlightBox[]>([]);
  const [sheetEl, setSheetEl] = useState<HTMLDivElement | null>(null);
  const isDragging = useRef(false);
  const anchorRef = useRef<SelectionPoint | null>(null);
  const atomsRef = useRef(bodyAtoms);
  atomsRef.current = bodyAtoms;

  const atomOrder = useMemo(
    () => bodyAtoms.map((a) => a.id),
    [bodyAtoms],
  );
  const atomOrderRef = useRef(atomOrder);
  atomOrderRef.current = atomOrder;

  const sheetRef = useCallback<RefCallback<HTMLDivElement>>((node) => {
    setSheetEl(node);
  }, []);

  const applyRange = useCallback((range: SelectionRange | null) => {
    setSelectionRange(range);
    setHighlightBoxes(calculateHighlightBoxes(range, [...atomsRef.current]));
  }, []);

  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (e.button !== 0) return;
      const container = sheetEl;
      if (!container) return;

      isDragging.current = true;
      const { x, y } = pageCoords(e, container, pageSize.width, pageSize.height);
      const clicked = hitPoint(atomsRef.current, x, y);
      container.dataset.fasa27LastXy = `${x.toFixed(2)},${y.toFixed(2)}`;
      container.dataset.fasa27AtomCount = String(atomsRef.current.length);

      if (clicked) {
        anchorRef.current = clicked;
        container.dataset.fasa27Hit = `${clicked.atomId}:${clicked.wordIndex}`;
        applyRange(updateSelection(null, clicked, atomOrderRef.current));
      } else {
        anchorRef.current = null;
        container.dataset.fasa27Hit = 'miss';
        applyRange(null);
      }
    },
    [applyRange, pageSize.height, pageSize.width, sheetEl],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current || !anchorRef.current || !sheetEl) return;

      const { x, y } = pageCoords(e, sheetEl, pageSize.width, pageSize.height);
      const endPoint = hitPoint(atomsRef.current, x, y);
      if (!endPoint) return;

      const order = atomOrderRef.current;
      const fromAnchor = updateSelection(null, anchorRef.current, order);
      applyRange(updateSelection(fromAnchor, endPoint, order));
    },
    [applyRange, pageSize.height, pageSize.width, sheetEl],
  );

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    if (!sheetEl) return;

    sheetEl.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      sheetEl.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [sheetEl, handleMouseDown, handleMouseMove, handleMouseUp]);

  return { highlightBoxes, selectionRange, sheetRef };
}
